import mainConfig from "common/config/main.config";
import { Requests, serviceResultFactory } from "common/requests";
import { BadgePaymentStatus, BadgeStatuses, HttpMethod } from "../common/enums";
import { BadgeUpdate, ILogEvent } from "../common/types";
import {
  ENTRANTS,
  EVENT_DETAILS,
  EVENT_OVERVIEW_BY_PRODUCT,
  EVENT_SUMMARY,
  REG_AND_ATTENDANCE,
  SHOW_OVERVIEW,
  V1_REPORTS,
  ZONE_ACCESS_BY_HOUR,
  ZONE_ACCESS_BY_LOCATION,
  ZONE_ACCESSED_BY_HOUR
} from "common/constants";
import {
  ChangePaymentStatusBody,
  DeviceActivationPayload,
  GenerateQrCodeResponse,
  GetMercuryEventsPayload,
  LatestBadgeQrCodeResponse
} from "./types";
import { DeviceInfo } from "../components/devices/addDevice/types";
import DeviceActions from "../components/devices/addDevice/helpers/deviceActions/DeviceActions";
import { EncryptedBadge, FetchEventBadgesResponse, GetFreyaBadgesResponse, GetOrdersResponse } from "../redux/types";
import { v4 as uuid } from "uuid";
import dayjs from "../common/dayjs";
import { PaymentDataDTO } from "@reedexpo/mercury-badge-manager-components";
import { CustomersAndProductsData, ZoneAccessedByLocationData } from "../components/dashboard/utils/types";

const getShowSecret = async (eventEditionGbsCode: string) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(`${registrationApiUrl}/v1/eventeditions/${eventEditionGbsCode}/secret`)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getShowStatistics = (eventEditionGbsCode: string, params) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + EVENT_SUMMARY, params)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getEntrants = (body: { eventEditionGbsCode: string; eventLocalStartDate: string; eventLocalEndDate: string }) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${registrationApiUrl}${V1_REPORTS}${body.eventEditionGbsCode}${ENTRANTS}`;

  return Requests.getRequestWithBearer(url, body)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getRegAndAttendanceData = (body: { eventEditionGbsCode: string; eventLocalStartDate: string; eventLocalEndDate: string }) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${registrationApiUrl}${V1_REPORTS}${body.eventEditionGbsCode}${REG_AND_ATTENDANCE}`;

  return Requests.getRequestWithBearer(url, body)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getEventDetails = (body: { eventEditionGbsCode: string; eventLocalStartDate: string; eventLocalEndDate: string }) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${registrationApiUrl}${V1_REPORTS}${body.eventEditionGbsCode}${EVENT_DETAILS}`;

  return Requests.getRequestWithBearer(url, body)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getReportsShowOverview = (eventEditionGbsCode: string, params) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + SHOW_OVERVIEW, params)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getReportsZoneAccessedByHour = (eventEditionGbsCode: string, eventLocalStartDate: string, eventLocalEndDate: string) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + ZONE_ACCESSED_BY_HOUR, {
    eventEditionGbsCode,
    eventLocalStartDate,
    eventLocalEndDate
  })
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getReportsZoneAccessByHour = (eventEditionGbsCode: string, params) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + ZONE_ACCESS_BY_HOUR, params)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getReportsZoneAccessByLocation = (
  eventEditionGbsCode: string,
  eventLocalStartDate: string,
  eventLocalEndDate: string
): Promise<{ zoneAccessed: ZoneAccessedByLocationData[] }> => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + ZONE_ACCESS_BY_LOCATION, {
    eventEditionGbsCode,
    eventLocalStartDate,
    eventLocalEndDate
  })
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getReportsCustomersAndProducts = (
  eventEditionGbsCode: string,
  eventLocalStartDate: string,
  eventLocalEndDate: string
): Promise<{ eventOverview: CustomersAndProductsData[] }> => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  return Requests.getRequestWithBearer(registrationApiUrl + V1_REPORTS + eventEditionGbsCode + EVENT_OVERVIEW_BY_PRODUCT, {
    eventEditionGbsCode,
    eventLocalStartDate,
    eventLocalEndDate
  })
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const logBadgeDownloadedEvent = async (events: ILogEvent["event"][], eventEditionGbsCode: string, deviceId?: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const body = { eventEditionGbsCode, addBadgeDownloadEvents: events };
  if (deviceId && deviceId.length !== 0) {
    const requestHmac = await DeviceActions.getDeviceHmacFromBody(JSON.stringify(body));
    return Requests.restApiCallWithDeviceHmac(
      `${baseApiUrl}/v1/events/badgedownloads/byid/batch`,
      HttpMethod.POST,
      deviceId,
      requestHmac,
      body
    )
      .then(serviceResultFactory.fromRestApiResponse)
      .catch(serviceResultFactory.fromRestApiError);
  }

  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/events/badgedownloads/byid/batch`, HttpMethod.POST, body)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getSupportedMercuryShows = (): Promise<GetMercuryEventsPayload> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.getRequest(`${baseApiUrl}/v1/eventeditions`)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const generateQrCodeForDevice = (eventEditionGbsCode: string, deviceName: string): GenerateQrCodeResponse => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/devices`, HttpMethod.POST, {
    deviceName,
    eventEditionGbsCode
  })
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getAllDevices = (eventEditionGbsCode: string): Promise<DeviceInfo[]> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/devices?eventEditionGbsCode=${eventEditionGbsCode}`, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const deactivateDevice = (deviceInstanceId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/devices/${deviceInstanceId}/deactivation`, HttpMethod.PATCH, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const activateDeviceQrCode = (deviceInstanceId: string, payload: DeviceActivationPayload) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCall(`${baseApiUrl}/v1/devices/${deviceInstanceId}/activation`, HttpMethod.PATCH, payload)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const changeBadgePaymentStatus = (body: ChangePaymentStatusBody) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/${body.badgeId}/paymentstatus`;

  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body)
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const changeBadgeStatus = (badgeId: string, status: BadgeStatuses, note: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/${badgeId}/status`;
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, { status, note })
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getOdinBadges = async (gbsCode: string, token = "", lastModifiedDate = ""): Promise<FetchEventBadgesResponse> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const tokenParam = `&token=${token}`;
  const lastModifiedDateParam = lastModifiedDate ? `&modifiedDate=${lastModifiedDate}` : "";
  const url = `${baseApiUrl}/v1/badges?eventEditionGbsCode=${gbsCode}${lastModifiedDateParam}${tokenParam}`;

  return Requests.getRequestWithBearer(url).then(serviceResultFactory.fromRestApiResponse).catch(serviceResultFactory.fromRestApiError);
};
const getFreyaBadges = async (deviceId: string, gbsCode: string, token = "", lastModifiedDate = ""): Promise<GetFreyaBadgesResponse> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const lastModifiedDateParam = lastModifiedDate ? `&modifiedDate=${lastModifiedDate}` : "";
  const tokenParam = `&token=${token}`;

  const url = `${baseApiUrl}/v1/badges/encrypted?eventEditionGbsCode=${gbsCode}${lastModifiedDateParam}${tokenParam}`;
  const requestHmac = await DeviceActions.getDeviceHmac(url, { handleUrl: true });

  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.GET, deviceId, requestHmac).then(serviceResultFactory.fromRestApiResponse);
};

const getFreyaBadgesShortened = async (
  deviceId: string,
  gbsCode: string,
  token = "",
  lastModifiedDate = ""
): Promise<GetFreyaBadgesResponse> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const lastModifiedDateParam = lastModifiedDate ? `&modifiedDate=${dayjs(lastModifiedDate).format("YYYY-MM-DDTHH:mm:ss[Z]")}` : "";
  const tokenParam = `&token=${token}`;

  const url = `${baseApiUrl}/v1/badges/encryptedforprint?eventEditionGbsCode=${gbsCode}${lastModifiedDateParam}${tokenParam}`;
  const requestHmac = await DeviceActions.getDeviceHmac(url, { handleUrl: true });

  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.GET, deviceId, requestHmac).then(serviceResultFactory.fromRestApiResponse);
};

const getLatestFreyaBadgeQrCode = async (deviceId: string, qrCodeContent: string): Promise<LatestBadgeQrCodeResponse> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/qrcode/latest`;
  const body = { qrCodeContent };
  const correlationId = uuid();
  const requestHmac = await DeviceActions.getDeviceHmac(`${deviceId}_${correlationId}`);

  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.POST, deviceId, requestHmac, body, correlationId).then(
    serviceResultFactory.fromRestApiResponse
  );
};

const getEncryptedFreyaBadgeById = async (deviceId: string, badgeId: string): Promise<EncryptedBadge> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  const url = `${baseApiUrl}/v1/badges/encryptedforprint/${badgeId}`;
  const correlationId = uuid();
  const requestHmac = await DeviceActions.getDeviceHmac(`${deviceId}_${correlationId}`);
  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.GET, deviceId, requestHmac, undefined, correlationId).then(
    serviceResultFactory.fromRestApiResponse
  );
};

const updateBadgesBatch = (eventEditionGbsCode: string, badges: BadgeUpdate[], correlationId?: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/updates/batch`;
  const body = { eventEditionGbsCode, badges };

  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body, correlationId);
};

const setOrderForUnpaidBadges = (body: { orderId: string; eventEditionGbsCode: string; badgeIds: string[] }) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  const reqUrl = `${registrationApiUrl}/v1/orders/badges`;
  return Requests.restApiCallWithBearer(reqUrl, HttpMethod.POST, body);
};

const getBadgeOrders = (eventEditionGbsCode: string, token = "", createdDate = ""): Promise<GetOrdersResponse> => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  let url = `${baseApiUrl}/v1/badges/orders?token=${token}&eventEditionGbsCode=${eventEditionGbsCode}`;

  if (createdDate) {
    url = `${url}&createdDate=${createdDate}`;
  }

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {}).then(serviceResultFactory.fromRestApiResponse);
};

const getBlockedEmails = (eventEditionGbsCode: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/registrants/blocked?eventEditionGbsCode=${eventEditionGbsCode}`;

  return Requests.getRequestWithBearer(url);
};

const getApprovedList = (eventEditionGbsCode: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/registrants/preapproved?eventEditionGbsCode=${eventEditionGbsCode}`;

  return Requests.getRequestWithBearer(url);
};

const resendEmail = (badgeId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/resendemail`;

  return Requests.restApiCallWithBearer(url, HttpMethod.POST, { badgeId });
};

const createRegistration = async (body: object, correlationId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/registrations`;
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body, correlationId);
};

const createContractorRegistration = async (body: object, correlationId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/Registrations/contractor`;
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body, correlationId);
};

const updateBadgePhoto = async (badgeId: string, deviceId: string, body: any) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const correlationId = uuid();

  const url = `${baseApiUrl}/v1/badges/${badgeId}/photos`;
  const requestHmac = await DeviceActions.getDeviceHmac(deviceId + "_" + correlationId);
  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.POST, deviceId, requestHmac, body, correlationId);
};

const updateBadgePhotoWithBearer = async (badgeId: string, body: any) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  const url = `${baseApiUrl}/v1/badges/${badgeId}/photos`;
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body);
};

const getBadgePhoto = async (badgeId: string, deviceId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const correlationId = uuid();

  const url = `${baseApiUrl}/v1/badges/${badgeId}/photos`;
  const requestHmac = await DeviceActions.getDeviceHmac(deviceId + "_" + correlationId);
  return Requests.restApiCallWithDeviceHmac(url, HttpMethod.GET, deviceId, requestHmac, {}, correlationId);
};

const updatePaymentStatus = async (badgeId: string, paymentStatus: BadgePaymentStatus, authorizedById: string, note?: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/${badgeId}/paymentstatus`;
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, { badgeId, paymentStatus, authorizedById, note });
};

const getCompanyEntitlements = async (orgId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/entitlements?participatingOrganisationAtlasCode=${orgId}`;
  return Requests.restApiCallWithBearer(url, HttpMethod.GET);
};

const getBadgePhotoWithBearer = async (badgeId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/badges/${badgeId}/photos`, HttpMethod.GET);
};

const getExhibitingOrganisations = async (eventEditionId: string, first?: number, offset?: number) => {
  const registrationApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const params = `${Number.isInteger(first) ? `&first=${first}` : ""}${Number.isInteger(offset) ? `&offset=${offset}` : ""}`;
  return Requests.restApiCallWithBearer(
    `${registrationApiUrl}/v1/exhibitingOrganisations?eventEditionId=${eventEditionId}${params}`,
    HttpMethod.GET
  );
};

const postPartialPayments = (payload: PaymentDataDTO) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  return Requests.restApiCallWithBearer(`${baseApiUrl}/v1/payments/group`, HttpMethod.POST, payload.paymentGroup);
};

const updateBadge = (payload: BadgeUpdate, correlationId?: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/upgrade/onsite/${payload.badgeId}`;
  return Requests.restApiCallWithBearer(url, HttpMethod.PATCH, payload, correlationId);
};

const getBadgeOrdersByBadgeId = async (badgeId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/${badgeId}/orders`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getBadge = async (badgeId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/badges/${badgeId}`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

export const saveReceipt = async (file: any, eventEditionGbsCode: string, orderId?: string, badgeId?: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/receipts/orders/${orderId}?eventEditionGbsCode=${eventEditionGbsCode}&badgeId=${badgeId}`;

  const formData = new FormData();
  formData.append("file", file);
  return Requests.restApiCallWithBearer(url, HttpMethod.POST, formData);
};

export const reprintReceiptWithBearer = async (receiptId: string, eventEditionGbsCode: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/receipts/${receiptId}?eventEditionGbsCode=${eventEditionGbsCode}`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET);
};

export const getReceipt = async (orderIds: string[], eventEditionGbsCode: string, badgeId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/receipts/batch`;
  const body = { eventEditionGbsCode, orderIds, badgeId };

  return Requests.restApiCallWithBearer(url, HttpMethod.POST, body);
};

const getCompanyAdmins = async (participatingOrganisationId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/exhibitingOrganisations/${participatingOrganisationId}/admins`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getCompanySharers = async (participatingOrganisationId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;
  const url = `${baseApiUrl}/v1/exhibitingOrganisations/${participatingOrganisationId}/sharers`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const getLeadManagerCode = async (participatingOrganisationId: string) => {
  const baseApiUrl = mainConfig.getConfig(process.env.REACT_APP_ENVIRONMENT).registrationApiUrl;

  const url = `${baseApiUrl}/v1/exhibitingOrganisations/${participatingOrganisationId}/leadManagerCode`;

  return Requests.restApiCallWithBearer(url, HttpMethod.GET, {})
    .then(serviceResultFactory.fromRestApiResponse)
    .catch(serviceResultFactory.fromRestApiError);
};

const PublicApiService = {
  postPartialPayments,
  updateBadge,
  getShowSecret,
  getFreyaBadges,
  getOdinBadges,
  getBadgeOrders,
  getShowStatistics,
  getReportsShowOverview,
  getReportsZoneAccessByHour,
  getReportsZoneAccessedByHour,
  getReportsZoneAccessByLocation,
  getReportsCustomersAndProducts,
  logBadgeDownloadedEvent,
  getSupportedMercuryShows,
  generateQrCodeForDevice,
  activateDeviceQrCode,
  getAllDevices,
  deactivateDevice,
  updateBadgesBatch,
  getBadgePhoto,
  resendEmail,
  createRegistration,
  createContractorRegistration,
  updateBadgePhoto,
  updatePaymentStatus,
  getBadgePhotoWithBearer,
  updateBadgePhotoWithBearer,
  changeBadgePaymentStatus,
  setOrderForUnpaidBadges,
  getCompanyEntitlements,
  getFreyaBadgesShortened,
  getBlockedEmails,
  getApprovedList,
  changeBadgeStatus,
  getEntrants,
  getRegAndAttendanceData,
  getEventDetails,
  getEncryptedFreyaBadgeById,
  getLatestFreyaBadgeQrCode,
  getBadgeOrdersByBadgeId,
  getBadge,
  getExhibitingOrganisations,
  saveReceipt,
  reprintReceiptWithBearer,
  getReceipt,
  getCompanyAdmins,
  getCompanySharers,
  getLeadManagerCode
};

export default PublicApiService;
